import type { LenderYields } from '../../../../types/lenderTypes'

/**
 * Intrinsic yield after depositing `amountUSD`.
 * Derived from getIntrinsicYieldOpen with amountInUSD=0.
 */
export function getIntrinsicYieldDeposit(
  amountUSD: number,
  cashFlowDeposits: number,
  cashFlowDebt: number,
  deposits: number,
  debt: number,
  yieldParams: LenderYields
): [borrowApr: number, depositApr: number, apr: number] {
  const totalCashFlowDeposits = cashFlowDeposits + amountUSD * yieldParams.intrinsicYield
  const newCollat = deposits + amountUSD
  return [
    Math.abs(debt) < 0.01 ? 0 : cashFlowDebt / debt,
    totalCashFlowDeposits / newCollat,
    (totalCashFlowDeposits - cashFlowDebt) / (newCollat - debt),
  ]
}

/**
 * Intrinsic yield after borrowing `amountUSD`.
 * Derived from getIntrinsicYieldOpen with amountOutUSD=0.
 */
export function getIntrinsicYieldBorrow(
  amountUSD: number,
  cashFlowDeposits: number,
  cashFlowDebt: number,
  deposits: number,
  debt: number,
  yieldParams: LenderYields
): [borrowApr: number, depositApr: number, apr: number] {
  const totalCashFlowDebt = cashFlowDebt + amountUSD * yieldParams.intrinsicYield
  const newDebt = debt + amountUSD
  return [
    totalCashFlowDebt / newDebt,
    Math.abs(deposits) < 0.01 ? 0 : cashFlowDeposits / deposits,
    (cashFlowDeposits - totalCashFlowDebt) / (deposits - newDebt),
  ]
}

/**
 * Intrinsic yield after withdrawing `amountUSD`.
 * Derived from getIntrinsicYieldClose with amountOutUSD=0.
 */
export function getIntrinsicYieldWithdraw(
  amountUSD: number,
  cashFlowDeposits: number,
  cashFlowDebt: number,
  deposits: number,
  debt: number,
  yieldParams: LenderYields
): [borrowApr: number, depositApr: number, apr: number] {
  const totalCashFlowDeposits = cashFlowDeposits - amountUSD * yieldParams.intrinsicYield
  const newCollat = deposits - amountUSD
  return [
    Math.abs(debt) < 0.01 ? 0 : cashFlowDebt / debt,
    totalCashFlowDeposits / newCollat,
    (totalCashFlowDeposits - cashFlowDebt) / (newCollat - debt),
  ]
}

/**
 * Intrinsic yield after repaying `amountUSD`.
 * Derived from getIntrinsicYieldClose with amountInUSD=0.
 */
export function getIntrinsicYieldRepay(
  amountUSD: number,
  cashFlowDeposits: number,
  cashFlowDebt: number,
  deposits: number,
  debt: number,
  yieldParams: LenderYields
): [borrowApr: number, depositApr: number, apr: number] {
  const totalCashFlowDebt = cashFlowDebt - amountUSD * yieldParams.intrinsicYield
  const newDebt = debt - amountUSD
  return [
    Math.abs(newDebt) < 0.01 ? 0 : totalCashFlowDebt / newDebt,
    Math.abs(deposits) < 0.01 ? 0 : cashFlowDeposits / deposits,
    (cashFlowDeposits - totalCashFlowDebt) / (deposits - newDebt),
  ]
}
